import * as STD                                         from '@emersya/mpa-std-lib';
import CM                                               from '@emersya/mpa-components-manager-lib';

export type IInputImageValueSerialized = string;

export interface IInputImageComponentConfiguration extends IInputComponentConfiguration<
	HTMLImageElement,
	IInputImageValueSerialized
>
{
	defaultIcon : string;
	maxSize? : number; //5Mb by default
	forceBackgroundColor? : string | null;
};

export class InputImageComponent extends InputComponent<
	HTMLDivElement,
	HTMLImageElement,
	IInputImageValueSerialized,
	InputImageComponent,
	IInputImageComponentConfiguration
>
{
	static count = 0;
	component_DOM : HTMLDivElement;

	async onClick() {
		await (<any> window).app.transaction(async () => {
			const imagesFiles = await (STD.DOM.openFilesSelectorWithFilesTypes('image/png, image/jpeg, image/jpg'))();
			if (!imagesFiles || !imagesFiles.length) {
				this.currentCustomPicture = null;
			} else {
				const imageFile = imagesFiles[ 0 ] as File;
				if (imageFile.size > <number> this.component_configuration.maxSize) {
					return;
				}
				let newFile = null;
				if (this.component_configuration.forceBackgroundColor) {
					const imageUrl = URL.createObjectURL(imageFile);
					const img = new Image();
					img.src = imageUrl;
					await new Promise((resolve, reject) => {
						img.onload = resolve;
						img.onerror = reject;
					});


					const canvas = new STD.DOM.Canvas(img.width, img.height);
					canvas.context.fillStyle = this.component_configuration.forceBackgroundColor;
					canvas.context.fillRect(0, 0, img.width, img.height);
					canvas.context.drawImage(img, 0, 0);
					const mimeType = imageFile.type;
					if (![ 'image/png', 'image/jpeg' ].includes(mimeType)) {
						throw new Error;
					}
					const blob = await canvas.toBlob(<any> mimeType);
					newFile = new File([ blob ], imageFile.name, { type : mimeType });
				}
				try {
					const prefix = (<any> window).app.clientAppParameters.server.isOnProd ? '' : 'https://emersya.com';
					const appCode = (<any> window).app.clientAppParameters.server.isOnProd ? (<any> window).app.code : 'E3GC4QDIZW';
					const uploadResult = await fetch(`${ prefix }/en/standing/stand2024/uploadImage`, {
						method : 'POST',
						body :   STD.DOM.formDataFromObject({
							image : newFile ? newFile : imageFile,
							appCode
						})
					});
					const uploadedImageURL = await uploadResult.text();
					const image = await STD.DOM.createImageFrom(uploadedImageURL);
					image.classList.add('picture');
					this.currentCustomPicture = image;
				} catch (e) {
					console.warn(e);
				}
			}
			const oldPicture = this.currentPicture;
			this.currentPicture = this.currentCustomPicture || this.emptyPicture;
			if (oldPicture !== this.currentPicture) {
				oldPicture.replaceWith(this.currentPicture);
			}
			await this.updateValue(this.currentCustomPicture);
		});
	};

	override async serialize() {
		if (this.value === null) {
			return (null);
		}
		if (!this.value.src.startsWith('blob:')) {
			return this.value.src;
		}
		const out = <IInputImageValueSerialized> await (
			((FR, blob) => new Promise((resolve, reject) => {
				FR.onloadend = () => resolve(<string> FR.result);
				FR.onerror = reject;
				FR.readAsDataURL(blob);
			}))(
				new FileReader(),
				await (await fetch(this.value.src)).blob()
			)
		);
		return (out);
	};

	async deserialize(value : string | HTMLImageElement | null) {
		if (typeof value !== 'string') {
			return (value);
		}
		return (await STD.DOM.createImageFrom(value));
	};

	async onValueUpdated() {  //eslint-disable-line require-await
		this.currentCustomPicture = this.value;
		const oldPicture = this.currentPicture;
		this.currentPicture = this.currentCustomPicture || this.emptyPicture;
		oldPicture.replaceWith(this.currentPicture);
	};

	async reset() {
		await this.updateValue(await this.deserialize(this.component_configuration.defaultValue || null));
	};

	override isValueTheDefault() {
		return (
			(!this.value && !this.defaultValue) ||
			(this?.value?.src === this?.defaultValue?.src)
		);
	};

	private emptyPicture : HTMLImageElement;
	private currentCustomPicture : HTMLImageElement | null = null;
	private currentPicture : HTMLImageElement;

	constructor(
		descriptor : ConstructorParameters<typeof CM.Component>[0],
		configuration : IInputImageComponentConfiguration
	)
	{
		super(descriptor, configuration);
		this.component_DOM = <HTMLDivElement> STD.DOM.factory({
			childs : [
				{
					tag :        'img',
					classList :  [ 'picture' ],
					attributes : {
						src : configuration.defaultIcon || ''
					}
				}
			],
			events : {
				click : this.onClick.bind(this)
			}
		});
		if (!this.component_configuration.maxSize) {
			this.component_configuration.maxSize = 5 * 1024 * 1024;
		}
		const trs = this.component_configuration[ '@translations' ] || {};
		const trUploadEntry = trs.upload;
		if (trUploadEntry) {
			const trUpload = this.component_defineTranslation('upload', trUploadEntry);
			this.component_DOM.appendChild(STD.DOM.factory({
				tag :        'button',
				classList :  [ 'upload' ],
				attributes : {
					'data-translation' : trUpload
				}
			}));
		}
		this.emptyPicture = this.component_DOM.querySelector('img.picture') as HTMLImageElement;
		this.currentPicture = this.emptyPicture;
		this.addEventListener('value', this.onValueUpdated.bind(this), {
			listener : this,
			subType :  this.static.type
		});
	};
};
export namespace NS {
	export const Component = InputImageComponent;
	export type Component = InputImageComponent;
	export type Configuration = IInputImageComponentConfiguration;
	export type ValueSerialized = IInputImageValueSerialized;
}
;
import './InputImage.scss';
import { InputComponent, IInputComponentConfiguration } from '../Input/Input';

CM.declare({
	id :   'input-image',
	type : InputImageComponent
});
